import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ImageLightboxService, LightboxImage, LightboxState } from '../../services/image-lightbox.service';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const CLICK_ZOOM_SCALE = 2.5;
const WHEEL_STEP = 0.25;

@Component({
  selector: 'app-image-lightbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-lightbox.component.html',
  styleUrls: ['./image-lightbox.component.scss'],
})
export class ImageLightboxComponent implements OnDestroy {
  state: LightboxState | null = null;
  scale = MIN_SCALE;
  translateX = 0;
  translateY = 0;

  private dragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private startTranslateX = 0;
  private startTranslateY = 0;
  private readonly sub: Subscription;

  constructor(private lightbox: ImageLightboxService) {
    this.sub = this.lightbox.state$.subscribe(state => {
      this.state = state;
      this.resetView();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get image(): LightboxImage | null {
    return this.state ? this.state.images[this.state.index] : null;
  }

  get hasMultiple(): boolean {
    return !!this.state && this.state.images.length > 1;
  }

  get counterLabel(): string {
    return this.state ? `${this.state.index + 1} / ${this.state.images.length}` : '';
  }

  private resetView(): void {
    this.scale = MIN_SCALE;
    this.translateX = 0;
    this.translateY = 0;
  }

  close(): void {
    this.lightbox.close();
  }

  next(): void {
    this.lightbox.next();
  }

  prev(): void {
    this.lightbox.prev();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.state) this.close();
  }

  @HostListener('document:keydown.arrowright')
  onArrowRight(): void {
    if (this.state) this.next();
  }

  @HostListener('document:keydown.arrowleft')
  onArrowLeft(): void {
    if (this.state) this.prev();
  }

  // Click zooms in centered roughly on the click point, or resets to fit if
  // already zoomed — a click-to-toggle affordance rather than requiring the
  // wheel, since not everyone views this on a trackpad/mouse with scroll.
  toggleZoom(event: MouseEvent): void {
    if (this.scale > MIN_SCALE) {
      this.resetView();
      return;
    }
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const offsetX = event.clientX - rect.left - rect.width / 2;
    const offsetY = event.clientY - rect.top - rect.height / 2;
    this.scale = CLICK_ZOOM_SCALE;
    this.translateX = (-offsetX * (this.scale - 1)) / this.scale;
    this.translateY = (-offsetY * (this.scale - 1)) / this.scale;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP;
    this.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale + delta));
    if (this.scale === MIN_SCALE) { this.translateX = 0; this.translateY = 0; }
  }

  onPointerDown(event: PointerEvent): void {
    if (this.scale <= MIN_SCALE) return;
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.startTranslateX = this.translateX;
    this.startTranslateY = this.translateY;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    this.translateX = this.startTranslateX + (event.clientX - this.dragStartX);
    this.translateY = this.startTranslateY + (event.clientY - this.dragStartY);
  }

  onPointerUp(): void {
    this.dragging = false;
  }
}
